// @flow
import { get, debounce } from 'lodash';
import moment from 'moment';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { subscribeAgenda } from '@helpers/realtime';

type Props = {
  onRefresh: () => void,
};

const isVisible = (date: ?string, filters: Object) => {
  if (!date) return false;

  const selectedDate = moment(get(filters, 'selectedDate') || undefined);
  const unit = get(filters, 'switchMode') === 'month' ? 'month' : 'isoWeek';
  return moment(date, 'YYYY-MM-DD').isBetween(selectedDate.clone().startOf(unit), selectedDate.clone().endOf(unit), 'day', '[]');
};

// Listens for appointment changes made elsewhere (other computers, the external API)
// and asks the agenda to refetch when one lands in the visible range. Renders nothing.
const AgendaLiveUpdates = ({ onRefresh }: Props) => {
  const clinicId = useSelector(state => get(state, ['context', 'clinics', 'activeClinic', 'id']));
  const token = useSelector(state => get(state, ['auth', 'token']));
  const filters = useSelector(state => get(state, ['context', 'appointments', 'filters'], {}));

  // Refs keep the socket alive across re-renders; only a clinic change reconnects.
  const tokenRef = useRef(token);
  const filtersRef = useRef(filters);
  const onRefreshRef = useRef(onRefresh);
  tokenRef.current = token;
  filtersRef.current = filters;
  onRefreshRef.current = onRefresh;

  const hasToken = Boolean(token);

  useEffect(() => {
    // Live updates are for the web agenda; the Cordova apps keep refreshing on navigation.
    if (window.isCordovaApp || !clinicId || !hasToken) return undefined;

    // A recurrence or bulk edit arrives as a burst of events: refetch once.
    const refresh = debounce(() => onRefreshRef.current(), 1000);

    const unsubscribe = subscribeAgenda(clinicId, () => tokenRef.current, {
      onChange: (change) => {
        if (isVisible(change.date, filtersRef.current) || isVisible(change.previousDate, filtersRef.current)) {
          refresh();
        }
      },
      onConnected: refresh,
    });

    return () => {
      refresh.cancel();
      unsubscribe();
    };
  }, [clinicId, hasToken]);

  return null;
};

export default AgendaLiveUpdates;
